import { useCallback, useState } from 'react';

/**
 * Lightweight per-field validation state for hand-rolled forms.
 *
 * Instead of one lumped "Name and role required" toast, each offending field
 * gets an inline message + red highlight so the user sees exactly what to fix.
 *
 * Usage:
 *   const fe = useFieldErrors<'name' | 'role'>();
 *   function submit() {
 *     if (!fe.validate({
 *       name: !name.trim() && 'Name is required',
 *       role: !roleId && 'Select a role',
 *     })) return;               // stops, fields now show errors
 *     mutate(...);
 *   }
 *   <FormGroup label="Name" error={fe.errors.name}>
 *     <Input invalid={fe.has('name')} value={name}
 *            onChange={(e) => { setName(e.target.value); fe.clear('name'); }} />
 *   </FormGroup>
 */
export function useFieldErrors<K extends string = string>() {
  const [errors, setErrors] = useState<Partial<Record<K, string>>>({});

  const has = useCallback((key: K) => Boolean(errors[key]), [errors]);

  const clear = useCallback((key?: K) => {
    setErrors((cur) => {
      if (!key) return {};
      if (!(key in cur)) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  }, []);

  /**
   * Run a set of checks. Each value is the error message when the field is
   * invalid, or a falsy value when it's fine. Sets the resulting errors and
   * returns true only if every field passed.
   */
  const validate = useCallback(
    (checks: Partial<Record<K, string | false | null | undefined>>): boolean => {
      const next: Partial<Record<K, string>> = {};
      (Object.keys(checks) as K[]).forEach((key) => {
        const val = checks[key];
        if (val) next[key] = val as string;
      });
      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [],
  );

  return { errors, setErrors, has, clear, validate };
}
